import { AsYouType, CountryCode, getCountryCallingCode } from 'libphonenumber-js'

import { COUNTRIES, CountryData } from '@/constants/countries'

export const getCountryByCode = (code: CountryCode): CountryData | undefined => {
  return COUNTRIES.find((country) => country.code === code)
}

export const cleanDigits = (value: string): string => {
  return value.replace(/[^\d]/g, '')
}

export const buildFullPhoneNumber = (countryCode: CountryCode, nationalNumber: string): string => {
  const digits = cleanDigits(nationalNumber)
  return `+${getCountryCallingCode(countryCode)}${digits}`
}

export const formatToNational = (input: string, countryCode: CountryCode): string => {
  const digits = cleanDigits(input)
  if (!digits) return ''

  const formatter = new AsYouType(countryCode)
  formatter.input(digits)

  return formatter.getNumber()?.formatNational() || digits
}

// Note: Parses an E.164(-ish) value into its country + nationally formatted number.
// Unlike a strict `parsePhoneNumber`, this is tolerant of *incomplete* numbers — e.g.,
// a draft loaded from storage, or a value synced from a parent while another field is
// still being typed into. It leans on `AsYouType` to detect the country from the dial
// code prefix and to format whatever national digits are present, rather than requiring
// a complete, valid number (which would otherwise resolve to `null` and blank the field).
export const parsePhoneValue = (
  value: string,
): { country: CountryData; nationalNumber: string } | null => {
  if (!value) return null

  const formatter = new AsYouType()
  formatter.input(value)

  const detectedCountry = formatter.getCountry()
  const callingCode = formatter.getCallingCode()

  // Prefer the exact detected country; otherwise fall back to the first country sharing
  // the dial code (e.g. `+1` is ambiguous across US/CA until an area code is entered).
  const getCountry = (): CountryData | undefined => {
    if (detectedCountry) return getCountryByCode(detectedCountry)
    if (callingCode) return COUNTRIES.find((item) => item.dialCode === `+${callingCode}`)
    return undefined
  }

  const country = getCountry()

  if (!country) return null

  const nationalDigits = formatter.getNationalNumber()

  return {
    country,
    nationalNumber: nationalDigits ? formatToNational(nationalDigits, country.code) : '',
  }
}
